import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, 
  Table, TableBody, TableCell, TableHead, TableRow, Avatar,
  Dialog, DialogTitle, DialogContent, IconButton, Collapse, Button, DialogActions,
  Divider
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';

import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import TradeFormFields from '../components/trades/TradeFormFields';
import { calculateTradeMetrics } from '../utils/tradeUtils';
import TradeFilters from '../components/common/TradeFilters';
import useTradeFilters from '../hooks/useTradeFilters';
import alertService from '../services/alertService';

// Smart symbol mapping for Binance
const createSymbolMapping = (userSymbol) => {
  // First try direct mapping with common symbols
  const mappings = {
    'BITCOIN': 'BTC',
    'BITCOIN / TETHERU': 'BTC',
    'BITCOIN / TETHERU$': 'BTC',
    'BITCOIN/TETHERU': 'BTC',
    'BITCOIN/TETHERU$': 'BTC',
    'ETHEREUM': 'ETH',
    'SOLANA': 'SOL',
    'CARDANO': 'ADA',
    'POLKADOT': 'DOT',
    'CHAINLINK': 'LINK',
    'LITECOIN': 'LTC',
    'BITCOIN CASH': 'BCH',
    'STELLAR': 'XLM',
    'DOGECOIN': 'DOGE',
    'TETHER': 'USDT',
    'TETHERU': 'USDT',
    'TETHERU$': 'USDT',
    'TETHERУ$': 'USDT',
    'USD COIN': 'USDC',
    'BINANCE COIN': 'BNB',
    'XRP': 'XRP',
    'AVALANCHE': 'AVAX',
    'POLYGON': 'MATIC',
    'UNISWAP': 'UNI',
    'APT': 'APT',
    'APT/USDT': 'APT',
    'APTOS': 'APT',
    'HYPE': 'HYPE',
    'HYPEUSDT': 'HYPE',
    'HYPE/USDT': 'HYPE',
    '1000PEPE': 'PEPE',
    '1000PEPE/USDT': 'PEPE',
    'PEPE': 'PEPE'
  };
  
  // Try direct mapping with uppercase symbol
  const symbol = userSymbol.toUpperCase();
  if (mappings[symbol]) {
    return mappings[symbol];
  }
  
  // Use normalize_symbol function for more comprehensive cleaning
  return normalizeSymbol(userSymbol);
};

// Comprehensive symbol normalization function
const normalizeSymbol = (inputStr) => {
  if (!inputStr) return '';
  
  // Handle special case for symbols with 1000 prefix
  if (inputStr.startsWith('1000')) {
    // Remove the 1000 prefix
    return inputStr.substring(4);
  }
  
  // Lowercase and remove separators
  let clean = inputStr.toLowerCase().replace(/[\/\-_\s]/g, '');
  
  // Replace known full names with their symbols
  const fullNames = {
    'bitcoin': 'btc',
    'ethereum': 'eth',
    'solana': 'sol',
    'ripple': 'xrp',
    'dogecoin': 'doge',
    'binancecoin': 'bnb',
    'cardano': 'ada',
    'polkadot': 'dot',
    'chainlink': 'link',
    'litecoin': 'ltc',
    'bitcoincash': 'bch',
    'stellar': 'xlm',
    'avalanche': 'avax',
    'polygon': 'matic',
    'uniswap': 'uni',
    'aptos': 'apt'
  };

  // Replace full names with symbols
  for (const [name, symbol] of Object.entries(fullNames)) {
    if (clean.startsWith(name)) {
      clean = clean.replace(name, symbol);
    }
    if (clean.endsWith(name)) {
      clean = clean.replace(name, symbol);
    }
  }
  
  // Remove common quote currencies
  clean = clean.replace('usdt', '').replace('usd', '').replace('busd', '');
  
  // Return uppercase for consistency with Binance API
  return clean.toUpperCase();
};

const WatchlistPage = () => {
  const [trades, setTrades] = useState([]);
  const [symbolInsights, setSymbolInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedSymbol, setExpandedSymbol] = useState(null);
  const [editTradeOpen, setEditTradeOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);

  const [strategies, setStrategies] = useState([]);
  const [strategyNamesMap, setStrategyNamesMap] = useState({});
  const [livePrices, setLivePrices] = useState({});
  const [symbolMappings, setSymbolMappings] = useState({});
  const [previewImage, setPreviewImage] = useState('');
  
  const [groupBy, setGroupBy] = useState('symbol');
  
  const {
    selectedCategory, selectedSymbol: filterSymbol, selectedStrategy, selectedStatus, selectedTags, startDate, endDate,
    setSelectedCategory, setSelectedSymbol: setFilterSymbol, setSelectedStrategy, setSelectedStatus, setSelectedTags, setStartDate, setEndDate,
    filteredTrades, symbols, statuses, allTags, handleTimeFilter
  } = useTradeFilters(trades);

  useEffect(() => {
    fetchTrades();
    fetchStrategies();
    
    // Listen for trade creation events
    const handleTradeCreated = () => {
      fetchTrades();
    };
    
    window.addEventListener('tradeCreated', handleTradeCreated);
    
    return () => {
      window.removeEventListener('tradeCreated', handleTradeCreated);
    };
  }, []);

  // WebSocket for live crypto prices
  useEffect(() => {
    const cryptoSymbols = symbolInsights.filter(insight => insight.categories.includes('crypto'));
    
    if (cryptoSymbols.length === 0) return;
    
    // Create symbol mappings
    const mappings = {};
    cryptoSymbols.forEach(insight => {
      const originalSymbol = insight.symbol;
      const mappedSymbol = createSymbolMapping(originalSymbol);
      mappings[originalSymbol] = mappedSymbol;
    });
    
    setSymbolMappings(mappings);
    
    // Make sure we're getting APT data by using a more specific stream
    const ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
    
    ws.onopen = () => {
      // WebSocket connected
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const priceUpdates = {};
        
        data.forEach(ticker => {
          const binanceSymbol = ticker.s; // e.g., BTCUSDT, SOLUSDT
          const baseSymbol = ticker.s.replace('USDT', '').replace('BTC', '').replace('ETH', ''); // e.g., BTC, SOL
          const lowerBinanceSymbol = binanceSymbol.toLowerCase(); // For case-insensitive matching
          
          // Check if symbol starts with 1000 and adjust price accordingly
          const needsPriceAdjustment = binanceSymbol.startsWith('1000');
          
          const priceData = {
            price: needsPriceAdjustment ? parseFloat(ticker.c) * 1000 : parseFloat(ticker.c),
            change: parseFloat(ticker.P),
            volume: parseFloat(ticker.v)
          };
          
          // Store under multiple formats for flexible matching
          priceUpdates[binanceSymbol] = priceData; // BTCUSDT
          priceUpdates[baseSymbol] = priceData; // BTC
          priceUpdates[ticker.s.replace('USDT', '')] = priceData; // BTC from BTCUSDT
          priceUpdates[lowerBinanceSymbol] = priceData; // btcusdt (lowercase)
          
          // Store normalized version of the symbol
          const normalizedSymbol = normalizeSymbol(binanceSymbol);
          if (normalizedSymbol) {
            priceUpdates[normalizedSymbol] = priceData;
            priceUpdates[normalizedSymbol + 'USDT'] = priceData;
          }
          
          // Handle special cases
          if (binanceSymbol === 'APTUSDT') {
            priceUpdates['APT/USDT'] = priceData;
            priceUpdates['APT'] = priceData;
            priceUpdates['APTOS'] = priceData;
          }
          
          if (binanceSymbol === 'HYPEUSDT') {
            priceUpdates['HYPE/USDT'] = priceData;
            priceUpdates['HYPE'] = priceData;
          }
          
          // Handle symbols with 1000 prefix
          if (binanceSymbol.startsWith('1000')) {
            const baseSymbol = binanceSymbol.substring(4); // Remove 1000 prefix
            const baseSymbolWithoutUSDT = baseSymbol.replace('USDT', '');
            
            // Store both with and without the 1000 prefix
            priceUpdates[baseSymbolWithoutUSDT] = priceData;
            priceUpdates[baseSymbolWithoutUSDT + '/USDT'] = priceData;
            priceUpdates[binanceSymbol.replace('USDT', '') + '/USDT'] = priceData;
          }
        });
        
        // Map user symbols to price data
        const mappedPrices = {};
        Object.entries(mappings).forEach(([userSymbol, binanceSymbol]) => {
          // Normalize both user symbol and mapped symbol
          const normalizedUserSymbol = normalizeSymbol(userSymbol);
          const normalizedBinanceSymbol = normalizeSymbol(binanceSymbol);
          
          // Try multiple matching strategies in order of specificity
          const priceData = priceUpdates[binanceSymbol + 'USDT'] || 
                           priceUpdates[binanceSymbol] || 
                           priceUpdates[normalizedBinanceSymbol] ||
                           priceUpdates[normalizedBinanceSymbol + 'USDT'] ||
                           priceUpdates[userSymbol] ||
                           priceUpdates[normalizedUserSymbol] ||
                           priceUpdates[normalizedUserSymbol + 'USDT'] ||
                           priceUpdates[userSymbol.replace(/[^A-Z0-9]/g, '')] ||
                           priceUpdates[userSymbol.replace(/\//g, '')] ||
                           priceUpdates[(binanceSymbol + 'USDT').toLowerCase()] ||
                           priceUpdates[userSymbol.toLowerCase()] ||
                           priceUpdates[userSymbol.replace(/\//g, '').toLowerCase()];
          
          if (priceData) {
            mappedPrices[userSymbol] = priceData;
          }
        });
        
        // Only update state if we have meaningful price data
        if (Object.keys(mappedPrices).length > 0) {
          setLivePrices(prevPrices => {
            // Only update if prices actually changed
            const hasChanges = Object.keys(mappedPrices).some(symbol => {
              const prev = prevPrices[symbol];
              const current = mappedPrices[symbol];
              return !prev || prev.price !== current.price || prev.change !== current.change;
            });
            
            const newPrices = hasChanges ? { ...prevPrices, ...mappedPrices } : prevPrices;
            
            // Check price alerts
            if (hasChanges) {
              alertService.checkPriceAlerts(newPrices);
            }
            
            return newPrices;
          });
        }
        
      } catch (error) {
        console.error('Error parsing WebSocket data:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = (event) => {
      // WebSocket closed
    };
    
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [symbolInsights]);
  
  useEffect(() => {
    generateInsights(filteredTrades);
    // Update alerts when trades change
    alertService.updateAlerts(filteredTrades);
  }, [filteredTrades]);

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(res.data);
      // Update alerts when trades are fetched
      alertService.updateAlerts(res.data);
    } catch (err) {
      console.error('Error fetching trades:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(res.data);
      const strategyMap = {};
      for (const strat of res.data) {
        strategyMap[strat.strategyId] = strat.name;
      }
      setStrategyNamesMap(strategyMap);
    } catch (err) {
      console.error('Error fetching strategies:', err);
    }
  };

  const handleEditTrade = (trade) => {
    setEditingTrade({
      ...trade,
      category: trade.category || 'stock',
      leverage: Number(trade.leverage) || 1,
      positionSize: Number(trade.positionSize) || '',
      strategyIds: trade.strategyIds || [],
      tags: trade.tags || '',
      rating: trade.rating || 3,
      exchange: trade.exchange || ''
    });
    setEditTradeOpen(true);
  };
  

  
  const handleDeleteTrade = useCallback(async (tradeId) => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/deleteTrade', {
        tradeId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditTradeOpen(false);
      setEditingTrade(null);
      await fetchTrades();
    } catch (error) {
      console.error('Error deleting trade', error);
    }
  }, [fetchTrades]);

  const handleTradeChange = (field, value) => {
    setEditingTrade(prev => {
      const updated = { ...prev, [field]: value };
      
      // Only auto-calculate P&L if manual override is not enabled
      if (!updated.manualPnL && field === 'status') {
        const reward = parseFloat(updated.rewardAmount);
        const risk = parseFloat(updated.riskAmount);
        const status = value;
        
        if (!isNaN(reward) && !isNaN(risk)) {
          const pnl =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' : '';
          updated.profitLoss = pnl;
          updated.pnl = pnl;
        }
      }
      
      return updated;
    });
  };

  const handleSaveTrade = async () => {
    try {
      const token = localStorage.getItem('idToken');
      
      // Calculate metrics using shared utility function
      const metrics = calculateTradeMetrics(
        editingTrade.entryPrice,
        editingTrade.stopLoss,
        editingTrade.exitPrice,
        editingTrade.positionSize,
        editingTrade.leverage
      );
      
      // Calculate profit/loss based on status
      if (!editingTrade.manualPnL) {
        const reward = parseFloat(metrics.rewardAmount);
        const risk = parseFloat(metrics.riskAmount);
        const status = editingTrade.status;

        if (!isNaN(reward) && !isNaN(risk)) {
          editingTrade.profitLoss =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' :
            '';
          editingTrade.pnl = editingTrade.profitLoss;
        }
      }
      
      const payload = {
        ...editingTrade,
        ...metrics,
        rrSummary: metrics.riskRewardRatio ? `R:R = ${metrics.riskRewardRatio} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}` : ''
      };
      
      // Save trade
      
      const response = await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Trade saved
      
      setEditTradeOpen(false);
      setEditingTrade(null);
      await fetchTrades();
    } catch (err) {
      console.error('Error updating trade:', err);
      console.error('Error details:', err.response?.data || err.message);
    }
  };

  const generateInsights = (tradesData = filteredTrades) => {
    const symbolStats = {};
    
    tradesData.forEach(trade => {
      const symbol = trade.symbol;
      if (!symbol) return;
      
      if (!symbolStats[symbol]) {
        symbolStats[symbol] = {
          symbol,
          totalTrades: 0,
          wins: 0,
          losses: 0,
          totalPnL: 0,
          avgRating: 0,
          categories: new Set(),
          exchanges: new Set(),
          lastTradeDate: null,
          avgRiskReward: 0,
          riskRewardSum: 0,
          ratingSum: 0,
          allTradesCount: 0
        };
      }
      
      const stats = symbolStats[symbol];
      stats.allTradesCount++;
      stats.categories.add(trade.category);
      if (trade.exchange) stats.exchanges.add(trade.exchange);
      
      // Skip ANALYSIS trades for win rate, P&L and other calculations
      if (trade.status === 'ANALYSIS') return;
      
      stats.totalTrades++;
      if (trade.status === 'WIN') stats.wins++;
      if (trade.status === 'LOSS') stats.losses++;
      
      const pnl = parseFloat(trade.pnl || trade.profitLoss || 0);
      if (!isNaN(pnl)) stats.totalPnL += pnl;
      
      const rating = parseFloat(trade.rating || 0);
      if (rating > 0) {
        stats.ratingSum += rating;
      }
      
      const rr = parseFloat(trade.riskRewardRatio || 0);
      if (rr > 0) {
        stats.riskRewardSum += rr;
      }
      
      if (!stats.lastTradeDate || new Date(trade.tradeDate) > new Date(stats.lastTradeDate)) {
        stats.lastTradeDate = trade.tradeDate;
      }
    });
    
    const insights = Object.values(symbolStats).map(stats => ({
      ...stats,
      winRate: stats.totalTrades > 0 ? ((stats.wins / stats.totalTrades) * 100).toFixed(1) : 0,
      avgRating: stats.totalTrades > 0 ? (stats.ratingSum / stats.totalTrades).toFixed(1) : 0,
      avgRiskReward: stats.totalTrades > 0 ? (stats.riskRewardSum / stats.totalTrades).toFixed(2) : 0,
      categories: Array.from(stats.categories),
      exchanges: Array.from(stats.exchanges)
    })).sort((a, b) => b.totalTrades - a.totalTrades);
    
    setSymbolInsights(insights);
  };

  const getPerformanceColor = (pnl) => {
    if (pnl > 0) return '#4caf50';
    if (pnl < 0) return '#f44336';
    return '#ff9800';
  };

  const getWinRateColor = (winRate) => {
    if (winRate >= 70) return '#4caf50';
    if (winRate >= 50) return '#ff9800';
    return '#f44336';
  };

  const renderSymbolRow = (insight) => (
    <React.Fragment key={insight.symbol}>
      <TableRow hover>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small" 
              onClick={() => setExpandedSymbol(expandedSymbol === insight.symbol ? null : insight.symbol)}
            >
              {expandedSymbol === insight.symbol ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {insight.symbol.charAt(0)}
            </Avatar>
            <Typography 
              variant="subtitle2" 
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => {
                setSelectedSymbol(insight.symbol);
                setDetailsOpen(true);
              }}
            >
              {insight.symbol}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip 
            label={`${insight.totalTrades}/${insight.allTradesCount}`} 
            size="small" 
            icon={<ShowChartIcon />}
            title="Completed trades / All trades (including analysis)"
          />
        </TableCell>
        <TableCell>
          <Chip 
            label={`${insight.winRate}%`}
            size="small"
            sx={{ bgcolor: getWinRateColor(insight.winRate), color: 'white' }}
            icon={insight.winRate >= 50 ? <TrendingUpIcon /> : <TrendingDownIcon />}
          />
        </TableCell>
        <TableCell>
          <Typography 
            variant="body2" 
            sx={{ color: getPerformanceColor(insight.totalPnL), fontWeight: 'bold' }}
          >
            ${insight.totalPnL.toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell>
          {insight.categories.includes('crypto') ? (() => {
            const getPriceData = () => {
              if (livePrices[insight.symbol]) {
                return livePrices[insight.symbol];
              }
              const normalizedSymbol = normalizeSymbol(insight.symbol);
              if (livePrices[normalizedSymbol]) {
                return livePrices[normalizedSymbol];
              }
              if (insight.symbol === 'APT/USDT' && livePrices['APT']) {
                return livePrices['APT'];
              } 
              if ((insight.symbol === 'HYPEUSDT' || insight.symbol === 'HYPE/USDT') && livePrices['HYPE']) {
                return livePrices['HYPE'];
              }
              if ((insight.symbol === '1000PEPE/USDT' || insight.symbol === '1000PEPE') && livePrices['PEPE']) {
                return livePrices['PEPE'];
              }
              return null;
            };
            const priceData = getPriceData();
            if (priceData) {
              const getDisplaySymbol = () => {
                if (insight.symbol === 'APT/USDT') {
                  return 'APTUSDT';
                } else if (insight.symbol === 'HYPEUSDT' || insight.symbol === 'HYPE/USDT') {
                  return 'HYPEUSDT';
                } else if (insight.symbol === '1000PEPE/USDT' || insight.symbol === '1000PEPE') {
                  return 'PEPEUSDT';
                } else {
                  const mappedSymbol = symbolMappings[insight.symbol] || normalizeSymbol(insight.symbol);
                  return mappedSymbol + 'USDT';
                }
              };
              return (
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    ${priceData.price.toFixed(4)}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: priceData.change >= 0 ? '#4caf50' : '#f44336',
                      fontWeight: 'bold'
                    }}
                  >
                    {priceData.change >= 0 ? '+' : ''}{priceData.change.toFixed(2)}%
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                    ({getDisplaySymbol()})
                  </Typography>
                </Box>
              );
            } else {
              return (
                <Typography variant="body2" color="text.secondary">
                  N/A
                </Typography>
              );
            }
          })() : (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          )}
        </TableCell>
        <TableCell>{insight.avgRiskReward}</TableCell>
        <TableCell>
          <Chip 
            label={`${insight.avgRating}/5`} 
            size="small" 
            color={insight.avgRating >= 4 ? 'success' : insight.avgRating >= 3 ? 'warning' : 'error'}
          />
        </TableCell>
        <TableCell>
          {insight.categories.map(cat => (
            <Chip key={cat} label={cat} size="small" variant="outlined" sx={{ mr: 0.5 }} />
          ))}
        </TableCell>
        <TableCell>
          <IconButton 
            size="small" 
            onClick={() => setExpandedSymbol(expandedSymbol === insight.symbol ? null : insight.symbol)}
          >
            {expandedSymbol === insight.symbol ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={expandedSymbol === insight.symbol} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Recent Trades for {insight.symbol}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Entry</TableCell>
                    <TableCell>Exit</TableCell>
                    <TableCell>Stop Loss</TableCell>
                    <TableCell>P&L</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>Live Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTrades
                    .filter(trade => trade.symbol === insight.symbol)
                    .slice(0, 5)
                    .map((trade) => (
                    <React.Fragment key={trade.tradeId}>
                      <TableRow>
                        <TableCell>{trade.tradeDate}</TableCell>
                        <TableCell>
                          <Chip 
                            label={trade.status} 
                            size="small" 
                            color={trade.status === 'WIN' ? 'success' : trade.status === 'LOSS' ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={trade.direction || 'N/A'} 
                            size="small" 
                            color={trade.direction === 'LONG' ? 'primary' : trade.direction === 'SHORT' ? 'secondary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>${trade.entryPrice}</TableCell>
                        <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                        <TableCell>${trade.stopLoss || 'N/A'}</TableCell>
                        <TableCell sx={{ color: parseFloat(trade.profitLoss) >= 0 ? 'green' : 'red' }}>
                          ${trade.profitLoss || '0'}
                        </TableCell>
                        <TableCell>{trade.rating || 'N/A'}/5</TableCell>
                        <TableCell>
                          {(trade.imageUrl || trade.screenshot) ? (
                            <IconButton 
                              size="small" 
                              onClick={() => setPreviewImage(trade.imageUrl || `data:image/png;base64,${trade.screenshot}`)}
                              sx={{ 
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': { bgcolor: 'primary.dark' }
                              }}
                            >
                              <ImageIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const liveStatus = calculateLiveStatus(trade);
                            return liveStatus ? (
                              <Box>
                                <Chip 
                                  label={liveStatus.message}
                                  size="small"
                                  color={liveStatus.status === 'success' ? 'success' : 
                                        liveStatus.status === 'danger' ? 'error' : 
                                        liveStatus.status === 'warning' ? 'warning' : 'default'}
                                  sx={{ mb: 0.5 }}
                                />
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                  Live: ${liveStatus.currentPrice}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    color: parseFloat(liveStatus.pnlPercent) >= 0 ? '#4caf50' : '#f44336',
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem'
                                  }}
                                >
                                  {parseFloat(liveStatus.pnlPercent) >= 0 ? '+' : ''}{liveStatus.pnlPercent}%
                                </Typography>
                              </Box>
                            ) : 'N/A';
                          })()
                        }
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleEditTrade(trade)}
                            title="Edit trade"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );

  const calculateLiveStatus = (trade) => {
    if (!['OPEN', 'ANALYSIS'].includes(trade.status)) return null;
    
    // Try to find price data using multiple strategies
    let priceData;
    
    // First try direct match
    priceData = livePrices[trade.symbol];
    
    // If not found, try with normalized symbol
    if (!priceData) {
      const normalizedSymbol = normalizeSymbol(trade.symbol);
      priceData = livePrices[normalizedSymbol];
    }
    
    // Special cases for specific symbols
    if (!priceData) {
      if (trade.symbol === 'APT/USDT' && livePrices['APT']) {
        priceData = livePrices['APT'];
      } else if ((trade.symbol === 'HYPEUSDT' || trade.symbol === 'HYPE/USDT') && livePrices['HYPE']) {
        priceData = livePrices['HYPE'];
      } else if (trade.symbol.startsWith('1000')) {
        // For symbols with 1000 prefix, try to find the base symbol without the prefix
        const baseSymbol = trade.symbol.substring(4).replace(/[\/\-_\s]/g, '');
        priceData = livePrices[baseSymbol];
      }
    }
    
    if (!priceData) return null;
    
    const currentPrice = priceData.price;
    const entryPrice = parseFloat(trade.entryPrice);
    const stopLoss = parseFloat(trade.stopLoss);
    const exitPrice = parseFloat(trade.exitPrice); // Take profit
    const direction = trade.direction || 'LONG'; // Default to LONG if not specified
    
    if (!entryPrice || !currentPrice) return null;
    
    // Calculate PnL based on direction
    let pnlPercent, pnlAmount;
    
    if (direction === 'LONG') {
      pnlPercent = ((currentPrice - entryPrice) / entryPrice) * 100;
      pnlAmount = (currentPrice - entryPrice) * parseFloat(trade.positionSize || 1);
    } else { // SHORT
      pnlPercent = ((entryPrice - currentPrice) / entryPrice) * 100;
      pnlAmount = (entryPrice - currentPrice) * parseFloat(trade.positionSize || 1);
    }
    
    let status = 'neutral';
    let message = '';
    
    // Check if hit stop loss
    if (stopLoss && ((direction === 'LONG' && currentPrice <= stopLoss) || 
                     (direction === 'SHORT' && currentPrice >= stopLoss))) {
      status = 'danger';
      message = 'At Stop Loss';
    }
    // Check if hit take profit
    else if (exitPrice && ((direction === 'LONG' && currentPrice >= exitPrice) || 
                          (direction === 'SHORT' && currentPrice <= exitPrice))) {
      status = 'success';
      message = 'At Take Profit';
    }
    // Check general profit/loss based on direction
    else if (pnlPercent > 2) {
      status = 'success';
      message = 'In Profit';
    }
    else if (pnlPercent < -2) {
      status = 'warning';
      message = 'In Loss';
    }
    else {
      status = 'neutral';
      message = 'Neutral';
    }
    
    return {
      status,
      message,
      pnlPercent: pnlPercent.toFixed(2),
      pnlAmount: pnlAmount.toFixed(2),
      currentPrice: currentPrice.toFixed(4)
    };
  };

  if (loading) return <Typography>Loading watchlist...</Typography>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        📊 Trading Watchlist
      </Typography>
      
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <TradeFilters
          selectedCategory={selectedCategory}
          selectedSymbol={filterSymbol}
          selectedStrategy={selectedStrategy}
          selectedStatus={selectedStatus}
          selectedTags={selectedTags}
          startDate={startDate}
          endDate={endDate}
          onCategoryChange={setSelectedCategory}
          onSymbolChange={setFilterSymbol}
          onStrategyChange={setSelectedStrategy}
          onStatusChange={setSelectedStatus}
          onTagsChange={setSelectedTags}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onTimeFilter={handleTimeFilter}
          symbols={symbols}
          strategies={strategies.map(s => s.strategyId)}
          statuses={statuses}
          allTags={allTags}
          strategyNamesMap={strategyNamesMap}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
        />
      </Paper>
      
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Symbols</Typography>
              <Typography variant="h3" color="primary">{symbolInsights.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Most Traded</Typography>
              <Typography variant="h5">{symbolInsights[0]?.symbol || 'N/A'}</Typography>
              <Typography variant="body2">{symbolInsights[0]?.totalTrades || 0} trades</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Best Performer</Typography>
              <Typography variant="h5">
                {symbolInsights.sort((a, b) => b.totalPnL - a.totalPnL)[0]?.symbol || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4caf50' }}>
                ${symbolInsights.sort((a, b) => b.totalPnL - a.totalPnL)[0]?.totalPnL.toFixed(2) || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: { xs: 1, sm: 2 }, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Symbol Performance</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{groupBy === 'symbol' ? 'Symbol' : groupBy === 'strategy' ? 'Strategy' : groupBy === 'date' ? 'Date' : 'Symbol'}</TableCell>
              <TableCell>Trades</TableCell>
              <TableCell>Win Rate</TableCell>
              <TableCell>P&L</TableCell>
              <TableCell>Live Price</TableCell>
              <TableCell>Avg R:R</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const filteredInsights = symbolInsights.filter(insight => 
                filteredTrades.some(trade => trade.symbol === insight.symbol)
              );
              
              if (groupBy === 'symbol') {
                return filteredInsights.map((insight) => renderSymbolRow(insight));
              }
              
              // Generate insights for each group
              const groupInsights = {};
              filteredTrades.forEach(trade => {
                const key = groupBy === 'strategy' ? (trade.strategyIds?.[0] || 'No Strategy') :
                           groupBy === 'date' ? (trade.tradeDate?.split('T')[0] || 'No Date') :
                           'Other';
                if (!groupInsights[key]) {
                  groupInsights[key] = {
                    key,
                    trades: [],
                    totalTrades: 0,
                    wins: 0,
                    losses: 0,
                    totalPnL: 0,
                    avgRating: 0,
                    categories: new Set(),
                    ratingSum: 0
                  };
                }
                
                const group = groupInsights[key];
                group.trades.push(trade);
                
                if (trade.status !== 'ANALYSIS') {
                  group.totalTrades++;
                  if (trade.status === 'WIN') group.wins++;
                  if (trade.status === 'LOSS') group.losses++;
                  
                  const pnl = parseFloat(trade.pnl || trade.profitLoss || 0);
                  if (!isNaN(pnl)) group.totalPnL += pnl;
                  
                  const rating = parseFloat(trade.rating || 0);
                  if (rating > 0) group.ratingSum += rating;
                }
                
                group.categories.add(trade.category);
              });
              
              // Sort groups by date (latest first)
              const sortedGroups = Object.entries(groupInsights).sort(([a], [b]) => {
                if (groupBy === 'date') {
                  return new Date(b) - new Date(a);
                }
                return a.localeCompare(b);
              });
              
              return sortedGroups.map(([groupKey, group]) => {
                const winRate = group.totalTrades > 0 ? ((group.wins / group.totalTrades) * 100).toFixed(1) : 0;
                const avgRating = group.totalTrades > 0 ? (group.ratingSum / group.totalTrades).toFixed(1) : 0;
                
                // Sort trades within group by date (latest first)
                const sortedTrades = group.trades.sort((a, b) => new Date(b.tradeDate) - new Date(a.tradeDate));
                
                return (
                  <React.Fragment key={groupKey}>
                    <TableRow hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => setExpandedSymbol(expandedSymbol === groupKey ? null : groupKey)}
                          >
                            {expandedSymbol === groupKey ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {groupBy === 'strategy' ? '🎯' : '📅'}
                          </Avatar>
                          <Typography variant="subtitle2">
                            {groupBy === 'strategy' ? (strategyNamesMap[groupKey] || groupKey) : groupKey}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${group.totalTrades}/${group.trades.length}`} 
                          size="small" 
                          icon={<ShowChartIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${winRate}%`}
                          size="small"
                          sx={{ bgcolor: getWinRateColor(winRate), color: 'white' }}
                          icon={winRate >= 50 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ color: getPerformanceColor(group.totalPnL), fontWeight: 'bold' }}
                        >
                          ${group.totalPnL.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>N/A</TableCell>
                      <TableCell>N/A</TableCell>
                      <TableCell>
                        <Chip 
                          label={`${avgRating}/5`} 
                          size="small" 
                          color={avgRating >= 4 ? 'success' : avgRating >= 3 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        {Array.from(group.categories).map(cat => (
                          <Chip key={cat} label={cat} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => setExpandedSymbol(expandedSymbol === groupKey ? null : groupKey)}
                        >
                          {expandedSymbol === groupKey ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={expandedSymbol === groupKey} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Trades in {groupBy === 'strategy' ? (strategyNamesMap[groupKey] || groupKey) : groupKey}
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Symbol</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Direction</TableCell>
                                  <TableCell>Entry</TableCell>
                                  <TableCell>Exit</TableCell>
                                  <TableCell>P&L</TableCell>
                                  <TableCell>Rating</TableCell>
                                  <TableCell>Image</TableCell>
                                  <TableCell>Live Status</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sortedTrades.map((trade) => (
                                  <TableRow key={trade.tradeId}>
                                    <TableCell>{trade.tradeDate}</TableCell>
                                    <TableCell>{trade.symbol}</TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={trade.status} 
                                        size="small" 
                                        color={trade.status === 'WIN' ? 'success' : trade.status === 'LOSS' ? 'error' : 'default'}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={trade.direction || 'N/A'} 
                                        size="small" 
                                        color={trade.direction === 'LONG' ? 'primary' : trade.direction === 'SHORT' ? 'secondary' : 'default'}
                                      />
                                    </TableCell>
                                    <TableCell>${trade.entryPrice}</TableCell>
                                    <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                                    <TableCell sx={{ color: parseFloat(trade.profitLoss) >= 0 ? 'green' : 'red' }}>
                                      ${trade.profitLoss || '0'}
                                    </TableCell>
                                    <TableCell>{trade.rating || 'N/A'}/5</TableCell>
                                    <TableCell>
                                      {(trade.imageUrl || trade.screenshot) ? (
                                        <IconButton 
                                          size="small" 
                                          onClick={() => setPreviewImage(trade.imageUrl || `data:image/png;base64,${trade.screenshot}`)}
                                          sx={{ 
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            '&:hover': { bgcolor: 'primary.dark' }
                                          }}
                                        >
                                          <ImageIcon fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {(() => {
                                        const liveStatus = calculateLiveStatus(trade);
                                        return liveStatus ? (
                                          <Box>
                                            <Chip 
                                              label={liveStatus.message}
                                              size="small"
                                              color={liveStatus.status === 'success' ? 'success' : 
                                                    liveStatus.status === 'danger' ? 'error' : 
                                                    liveStatus.status === 'warning' ? 'warning' : 'default'}
                                              sx={{ mb: 0.5 }}
                                            />
                                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                              Live: ${liveStatus.currentPrice}
                                            </Typography>
                                            <Typography 
                                              variant="caption" 
                                              sx={{ 
                                                display: 'block',
                                                color: parseFloat(liveStatus.pnlPercent) >= 0 ? '#4caf50' : '#f44336',
                                                fontWeight: 'bold',
                                                fontSize: '0.7rem'
                                              }}
                                            >
                                              {parseFloat(liveStatus.pnlPercent) >= 0 ? '+' : ''}{liveStatus.pnlPercent}%
                                            </Typography>
                                          </Box>
                                        ) : 'N/A';
                                      })()
                                    }
                                    </TableCell>
                                    <TableCell>
                                      <IconButton 
                                        size="small" 
                                        color="primary"
                                        onClick={() => handleEditTrade(trade)}
                                        title="Edit trade"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              });
            })()}

          </TableBody>
        </Table>
      </Paper>
      
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedSymbol} - All Trades
          <IconButton onClick={() => setDetailsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Entry Price</TableCell>
                <TableCell>Exit Price</TableCell>
                <TableCell>Stop Loss</TableCell>
                <TableCell>P&L</TableCell>
                <TableCell>R:R</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrades
                .filter(trade => trade.symbol === selectedSymbol)
                .map((trade) => (
                <TableRow key={trade.tradeId}>
                  <TableCell>{trade.tradeDate}</TableCell>
                  <TableCell>
                    <Chip 
                      label={trade.status} 
                      size="small" 
                      color={trade.status === 'WIN' ? 'success' : trade.status === 'LOSS' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{trade.direction}</TableCell>
                  <TableCell>${trade.entryPrice}</TableCell>
                  <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                  <TableCell>${trade.stopLoss}</TableCell>
                  <TableCell sx={{ color: parseFloat(trade.profitLoss) >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                    ${trade.profitLoss || '0'}
                  </TableCell>
                  <TableCell>{trade.riskRewardRatio}</TableCell>
                  <TableCell>{trade.rating || 'N/A'}/5</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {trade.notes || trade.insight || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setDetailsOpen(false);
                        handleEditTrade(trade);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
      
      <Dialog 
        open={!!previewImage} 
        onClose={() => setPreviewImage('')} 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Typography variant="h6">📊 Trade Chart</Typography>
          <IconButton 
            onClick={() => setPreviewImage('')}
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: 400
          }}>
            <img 
              src={previewImage} 
              alt="Trade Screenshot" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '80vh',
                borderRadius: 8
              }} 
            />
          </Box>
        </DialogContent>
      </Dialog>
      
      <Dialog 
        open={!!previewImage} 
        onClose={() => setPreviewImage('')} 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Typography variant="h6">📊 Trade Chart</Typography>
          <IconButton 
            onClick={() => setPreviewImage('')}
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: 400
          }}>
            <img 
              src={previewImage} 
              alt="Trade Screenshot" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '80vh',
                borderRadius: 8
              }} 
            />
          </Box>
        </DialogContent>
      </Dialog>
      
      <Dialog open={editTradeOpen} onClose={() => setEditTradeOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Edit Trade: {editingTrade?.symbol}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 3, sm: 3 }, pb: 3, px: { xs: 2, sm: 3 } }}>
          {editingTrade && (
            <Box sx={{ mt: 1 }}>
              <TradeFormFields
                values={editingTrade}
                onChange={handleTradeChange}
                strategies={strategies}
                trades={trades}
                defaultTab={1} // Start on the Detailed tab
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setEditTradeOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTrade}
            sx={{ minWidth: 100 }}
          >
            Save Changes
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleDeleteTrade(editingTrade?.tradeId)}
            startIcon={<DeleteIcon />}
            sx={{ minWidth: 100 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WatchlistPage;